/**
 * Modo debug: panel flotante con el estado en vivo de cada player y el log de
 * eventos enviados. Se activa con `?trk_video_debug=1`, con
 * `data-debug="true"` en el <script> o llamando `TrackriftVideo.debug(true)`.
 *
 * Existe para poder validar una landing ANTES de meter tráfico: si el panel
 * no enseña el player, el problema es la instrumentación, no el reporting.
 * Todo el CSS va inline y con prefijo propio para no chocar con la landing.
 */
import { activeSessions, onEvent, config, type Props, type VideoSession } from './core';

let panel: HTMLElement | null = null;
let logBox: HTMLElement | null = null;
let stateBox: HTMLElement | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let count = 0;

const COLORS: Record<string, string> = {
  video_impression: '#94a3b8',
  video_play: '#22c55e',
  video_progress: '#38bdf8',
  video_milestone: '#a78bfa',
  video_heartbeat: '#64748b',
  video_complete: '#f59e0b',
  video_exit: '#f87171',
  video_interaction: '#cbd5e1',
  video_cta_click: '#ec4899',
  video_cta_shown: '#f9a8d4',
  video_unlock: '#ec4899',
};

function el(tag: string, style: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  node.setAttribute('style', style);
  if (text != null) node.textContent = text;
  return node;
}

function fmt(n: unknown): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (v < 60) return `${Math.round(v)}s`;
  return `${Math.floor(v / 60)}m${String(Math.round(v % 60)).padStart(2, '0')}`;
}

function render(): void {
  if (!stateBox) return;
  const sessions = activeSessions();
  stateBox.innerHTML = '';
  if (!sessions.length) {
    stateBox.appendChild(el('div', 'color:#94a3b8;padding:6px 0', 'Ningún player detectado todavía.'));
    return;
  }
  for (const s of sessions) {
    const row = el('div', 'padding:6px 0;border-bottom:1px solid #1e293b');
    const badge = s.live ? '<span style="color:#f87171">● DIRECTO</span>' : '<span style="color:#64748b">VOD</span>';
    const pct = s.duration ? `${Math.round((s.position / s.duration) * 100)}%` : '—';
    row.innerHTML =
      `<div style="display:flex;gap:6px;align-items:center;justify-content:space-between">` +
      `<strong style="color:#e2e8f0">${s.id}</strong>` +
      `<span style="color:#64748b">${s.player} ${badge}</span></div>` +
      `<div style="color:#94a3b8;margin-top:3px">` +
      `${s.playing ? '<span style="color:#22c55e">▶ play</span>' : '⏸ pausa'} · ` +
      `pos ${fmt(s.position)}${s.duration ? ` / ${fmt(s.duration)} (${pct})` : ''}</div>` +
      `<div style="color:#94a3b8">visto <b style="color:#e2e8f0">${fmt(s.watched)}</b> · ` +
      `en directo <b style="color:#e2e8f0">${fmt(s.elapsed)}</b> · ` +
      `atento <b style="color:#e2e8f0">${fmt(s.active)}</b></div>` +
      `<div style="color:${s.inView ? '#22c55e' : '#f59e0b'}">` +
      `${s.inView ? 'en pantalla' : 'fuera de pantalla'}${s.muted ? ' · silenciado' : ''}` +
      `${s.rate !== 1 ? ` · ${s.rate}x` : ''}</div>`;
    stateBox.appendChild(row);
  }
}

function logEvent(name: string, props: Props, session: VideoSession): void {
  count += 1;
  if (!logBox) return;
  const color = COLORS[name] ?? '#e2e8f0';
  const detail = [
    props.percent != null ? `${props.percent}%` : null,
    props.threshold_seconds != null ? `hito ${fmt(props.threshold_seconds)}` : null,
    props.action != null ? String(props.action) : null,
    props.reason != null ? String(props.reason) : null,
    name === 'video_heartbeat' || name === 'video_exit'
      ? `visto ${fmt(props.seconds_watched)} · directo ${fmt(props.elapsed_seconds)}`
      : null,
    props.watched_ranges ? `tramos ${String(props.watched_ranges).slice(0, 40)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const line = el('div', 'padding:2px 0;border-bottom:1px solid #0f172a;line-height:1.35');
  const time = new Date().toLocaleTimeString('es-ES', { hour12: false });
  line.innerHTML =
    `<span style="color:#475569">${time}</span> ` +
    `<b style="color:${color}">${name}</b> ` +
    `<span style="color:#64748b">${session.id}</span>` +
    (detail ? `<div style="color:#94a3b8;padding-left:8px">${detail}</div>` : '');
  logBox.insertBefore(line, logBox.firstChild);
  while (logBox.childNodes.length > 120) logBox.removeChild(logBox.lastChild as Node);

  const header = document.getElementById('trk-video-debug-count');
  if (header) header.textContent = `${count} evento(s)`;
}

function build(): void {
  if (panel || typeof document === 'undefined') return;
  panel = el(
    'div',
    'position:fixed;right:12px;bottom:12px;z-index:2147483000;width:340px;max-height:70vh;' +
      'display:flex;flex-direction:column;background:#020617;color:#e2e8f0;border:1px solid #1e293b;' +
      'border-radius:10px;box-shadow:0 12px 32px rgba(0,0,0,.45);font:11px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;',
  );

  const head = el(
    'div',
    'display:flex;align-items:center;justify-content:space-between;gap:8px;padding:8px 10px;' +
      'border-bottom:1px solid #1e293b;background:#0f172a;border-radius:10px 10px 0 0;cursor:pointer',
  );
  head.innerHTML =
    '<strong style="color:#38bdf8">trackrift · vídeo</strong>' +
    '<span id="trk-video-debug-count" style="color:#64748b">0 evento(s)</span>';

  const body = el('div', 'overflow:auto;padding:8px 10px');
  stateBox = el('div', 'margin-bottom:8px');
  const sep = el('div', 'color:#475569;text-transform:uppercase;letter-spacing:.06em;margin:4px 0', 'eventos');
  logBox = el('div', '');
  body.appendChild(stateBox);
  body.appendChild(sep);
  body.appendChild(logBox);

  let collapsed = false;
  head.addEventListener('click', () => {
    collapsed = !collapsed;
    body.style.display = collapsed ? 'none' : 'block';
  });

  panel.appendChild(head);
  panel.appendChild(body);
  document.body.appendChild(panel);
}

/** Enciende/apaga el panel. Idempotente. */
export function setDebug(on: boolean): void {
  config.debug = on;
  if (!on) {
    if (timer) { clearInterval(timer); timer = null; }
    if (panel) { panel.remove(); panel = null; stateBox = null; logBox = null; }
    return;
  }
  if (typeof document === 'undefined') return;
  const start = () => {
    build();
    render();
    if (!timer) timer = setInterval(render, 500);
  };
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
}

let hooked = false;

/** Engancha el log una sola vez; el panel se pinta solo si debug está activo. */
export function installDebugHook(): void {
  if (hooked) return;
  hooked = true;
  onEvent((name, props, session) => {
    if (!config.debug) return;
    console.debug(`[trackrift-video] ${name}`, props);
    logEvent(name, props, session);
  });
}
